import { create } from 'zustand';
import { apiFetch } from '@/lib/api';
import type { TemplateStatus } from '@/lib/types';

export type TaskType = 'text_input' | 'photo' | 'checkbox' | 'action';

export interface VisitTemplate {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  instruction: string | null;
  followUp: string | null;
  cost: string | null;
  aiContext: string | null;
  nextVisitTemplateId: string | null;
  clientId: string | null;
  status: TemplateStatus;
  /** #RRGGBB — акцентный цвет шаблона */
  color: string | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TemplatePhoto {
  id: string;
  photoableType: string;
  photoableId: string;
  url: string;
  position: number;
  createdAt?: string;
}

export interface TaskTemplate {
  id: string;
  visitTemplateId: string;
  slug: string;
  position: number;
  type: TaskType;
  title: string;
  instruction: string | null;
  aiPrompts: string[] | null;
  isRequired: boolean;
}

export interface CreateTaskTemplateInput {
  /** Опционален: без slug бэк генерирует транслитом названия */
  slug?: string;
  position: number;
  type: TaskType;
  title: string;
  instruction?: string;
  ai_prompts?: string[];
  is_required?: boolean;
}

export interface CreateTemplateInput {
  /** Опционален: без названия бэк генерирует «Новый шаблон N» */
  title?: string;
  description?: string;
  instruction?: string;
  follow_up?: string;
  cost?: number;
  ai_context?: string;
  next_visit_template_id?: string;
  client_id?: string;
  /** #RRGGBB или null */
  color?: string | null;
  /** Задачи опциональны: шаблон создаётся в статусе new, задачи — позже */
  task_templates?: CreateTaskTemplateInput[];
}

interface TemplatesState {
  items: VisitTemplate[];
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
  fetchTemplates: () => Promise<void>;
  createTemplate: (input: CreateTemplateInput) => Promise<{ id: string } | null>;
}

export const useTemplatesStore = create<TemplatesState>()((set) => ({
  items: [],
  isLoading: false,
  isCreating: false,
  error: null,

  fetchTemplates: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiFetch('/api/v1/admin/templates');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const data = (await res.json()) as VisitTemplate[];
      set({ items: data, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Failed to load templates' });
    }
  },

  createTemplate: async (input) => {
    set({ isCreating: true, error: null });
    try {
      const res = await apiFetch('/api/v1/admin/templates', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Request failed: ${res.status}`);
      }
      const data = (await res.json()) as { id: string };
      set({ isCreating: false });
      return data;
    } catch (e) {
      set({
        isCreating: false,
        error: e instanceof Error ? e.message : 'Failed to create template',
      });
      return null;
    }
  },
}));

export const selectTemplates = (s: TemplatesState) => s.items;
export const selectTemplatesIsLoading = (s: TemplatesState) => s.isLoading;
export const selectTemplatesIsCreating = (s: TemplatesState) => s.isCreating;
export const selectTemplatesError = (s: TemplatesState) => s.error;
